/*
 *  Copyright (c) Qwoach LLC 2020. All rights reserved.
 *  Unauthorized copying of this file, via any medium is strictly prohibited
 *  Proprietary and confidential
 *  Created by Artem Bogush <artem@qwoach.com> on 18/12/2020
 */
import Migration from "@/utils/dataMigration/Migration";
import {isAppTheraLogik, isIterable} from "@/utils/helpers";
import ClientProfile from "@shared/Profile/ClientProfile";
import {plainToClass} from "class-transformer";
import BaseDataFilter from "@/core/js/backend/filters/BaseDataFilter";
import IntegrationStatus from "@shared/Billing/IntegrationStatus";
import CompanyIndexItem from "@shared/Company/CompanyIndexItem";
import {CoachingPackageType} from "@shared/CoachingPackage/CoachingPackageType";
import DataFilter from "@/core/js/backend/filters/DataFilter.js";
import {ClientStage} from "@shared/Profile/ClientStage";
import {isFreePlanId} from "@shared/SharedConstants";
import QuoteTemplateItem from "@shared/Template/QuoteTemplateItem";
import TlswSessionPlanCounterManager from "@tl/core/modules/commission/model/commission/TlswSessionPlanCounterManager";

export class Migration_24_25 extends Migration {

	constructor() {
		super(24, 25);
	}

	async migrate(dataManager, authManager) {
		console.log("migrating from 24 to 25")
		await dataManager.apiPostMigrateCurrentUserProfileDefaults()
		await authManager.fetchAndSyncUserProfile()
	}
}

export class Migration_23_24 extends Migration {

	constructor() {
		super(23, 24);
	}

	async migrate(dataManager, authManager) {
		console.log("migrating from 23 to 24")
		if(authManager.isCoach()){
			await this.migrateCoachGenograms(dataManager)
		} else if(authManager.isClient()){
			await this.migrateClientGenogram(dataManager)
		}
	}

	async migrateCoachGenograms(dataManager) {
		let dataFilter = new DataFilter(200)
		let pager = await dataManager.getGenogramItemListPager(dataFilter)
		while (pager.hasMore){
			let genogramItemList = await pager.next()
			let changedGenogramItemList = this.buildChangedGenogramItemList(genogramItemList)
			if(changedGenogramItemList.length){
				await dataManager.batchUpdateGenogramItem(changedGenogramItemList)
			}
		}
	}

	async migrateClientGenogram(dataManager) {
		await dataManager.apiPostRepairClientGenogramSharing()
	}

	buildChangedGenogramItemList(genogramItemList = []) {
		let changedGenogramItemList = []
		for(let genogramItem of genogramItemList){
			let clientId = genogramItem?.clientId
			let sharedWithIds = genogramItem?.sharedWithIds || []
			let hasClientSharing = clientId && sharedWithIds.includes(clientId)
			if(clientId && !hasClientSharing){
				genogramItem.setClientId(clientId)
				changedGenogramItemList.push(genogramItem)
			}
		}
		return changedGenogramItemList
	}
}

export class Migration_22_23 extends Migration {

	constructor() {
		super(22, 23);
	}

	async migrate(dataManager, authManager) {
		console.log("migrating from 22 to 23")
		if(!authManager.isCoach() || !isAppTheraLogik){
			return
		}
		let counterManager = new TlswSessionPlanCounterManager({dataManager})
		let dataFilter = new DataFilter(100)
		let pager = await dataManager.getClientProfileListPager(dataFilter)
		while (pager.hasMore){
			let clientProfileList = await pager.next()
			let clientProfilePatchList = []
			for (let clientProfile of clientProfileList) {
				let sessionCountMap = await counterManager.loadSessionCountMap(clientProfile.id)
				clientProfilePatchList.push(counterManager.createSessionCountPatch(clientProfile.id, sessionCountMap))
			}
			await dataManager.batchUpdateClientProfile(clientProfilePatchList)
		}
	}
}

export class Migration_21_22 extends Migration {

	constructor() {
		super(21, 22);
	}

	async migrate(dataManager, authManager) {
		console.log("migrating from 21 to 22")
		if(!authManager.isCoach()){
			return
		}
		let dataFilter = new DataFilter(200)
		let pager = await dataManager.getClientProfileListPager(dataFilter)
		while (pager.hasMore){
			let clientProfileList = await pager.next()
			await dataManager.batchUpdateClientProfile(clientProfileList)
		}
		if (isAppTheraLogik) {
			let userProfile = authManager.getUserProfile() || await dataManager.getUserProfile(authManager.getCoachId())
			let quoteTemplateItem = QuoteTemplateItem.createDefault(userProfile)
			await dataManager.setQuoteTemplate(quoteTemplateItem)
		}
	}

}

export class Migration_20_21 extends Migration {

	constructor() {
		super(20, 21);
	}

	async migrate(dataManager, authManager) {
		console.log("migrating from 20 to 21")
		if(authManager.isCoach()){
			let dataFilter = new DataFilter(200)

			let pager = await dataManager.getClientProfileListPager(dataFilter)

			while (pager.hasMore){
				let clientProfileList = await pager.next()
				await dataManager.batchUpdateClientProfile(clientProfileList)
			}
		}

		if(isAppTheraLogik && authManager.isCoach()){
			const THERALOGIK_FREE_PLAN_ID = "theralogik_free"
			const THERALOGIK_FREE_PLAN_NAME = "TheraLogik Free"
			const THERALOGIK_FREE_ACTIVE_CLIENT_LIMIT = 0

			let coachId = authManager.getCoachId()
			let userSubscription = await dataManager.getUserSubscription(coachId)
			if(userSubscription && isFreePlanId(userSubscription.planId) && userSubscription.planId !== THERALOGIK_FREE_PLAN_ID){
				userSubscription.planId = THERALOGIK_FREE_PLAN_ID
				userSubscription.name = THERALOGIK_FREE_PLAN_NAME
				userSubscription.price = 0
				userSubscription.pricePerMonth = 0
				userSubscription.features.activeClientLimit = THERALOGIK_FREE_ACTIVE_CLIENT_LIMIT
				userSubscription.featuresDescription = [
					{title: "- Unlimited quotes"},
					{title: "x No active commissions"},
					{title: "x No Smart Invoices™"},
					{title: "x No Smart ToDos™"},
				]
				await dataManager.updateUserSubscription(coachId, userSubscription)
				authManager.setUserSubscription(userSubscription)
			}
		}
	}

}

export class Migration_19_20 extends Migration {

	constructor() {
		super(19, 20);
	}

	async migrate(dataManager, authManager) {
		console.log("migrating from 19 to 20")
		if(authManager.isCoach()){
			//we load from db and set it back to trigger migrateSchema to v6
			let userProfile = await dataManager.getUserProfile(authManager.getCoachId())
			authManager.updateUserProfile(userProfile)
		}
	}

}

export class Migration_18_19 extends Migration {

	constructor() {
		super(18, 19);
	}

	async migrate(dataManager, authManager) {
		console.log("migrating from 18 to 19")

		if(authManager.isCoach()){

			// this migration applies id as filterable field
			// it only requires to fetch and save the entries back
			// since filterable fields are applied in the "beforeSaveToDb" method

			let dataFilter = new DataFilter(100)

			let pager = await dataManager.getClientProfileListPager(dataFilter)

			while (pager.hasMore){
				let clientProfileList = await pager.next()
				await dataManager.batchUpdateClientProfile(clientProfileList)
			}
		}

	}

}

export class Migration_17_18 extends Migration {


	constructor() {
		super(17, 18);
	}

	async migrate(dataManager, authManager) {
		console.log("migrating from 17 to 18")

		if(authManager.isCoach()){
			await dataManager.apiPostMigrateDriveStatsToUsageStats()
		}

	}
}

export class Migration_16_17 extends Migration {


	constructor() {
		super(16, 17);
	}

	async migrate(dataManager, authManager) {
		console.log("migrating from 16 to 17")
		if(authManager.isCoach()){

			//it is identical to migration 15_16, but we need to run it again, because there was bug in where clause which is no fixed, but we still need to apply it again.

			let dataFilter = new DataFilter()
				.where("clientStage", "in", ["CLIENT", "PROSPECT"])

			let pager = await dataManager.getClientProfileListPager(dataFilter)

			while (pager.hasMore){
				let clientProfileList = await pager.next()
				for(let clientProfile of clientProfileList){
					clientProfile.clientStage = clientProfile.clientStage.toLowerCase()
				}
				console.log("migrate: clientProfileList = ", clientProfileList)
				await dataManager.batchUpdateClientProfile(clientProfileList)
			}
		}

	}
}

export class Migration_15_16 extends Migration {


	constructor() {
		super(15, 16);
	}

	async migrate(dataManager, authManager) {
		console.log("migrating from 15 to 16")
		if(authManager.isCoach()){

			let dataFilter = new DataFilter()
				.where("clientStage", "in", ["CLIENT", "PROSPECT"])

			let pager = await dataManager.getClientProfileListPager(dataFilter)

			while (pager.hasMore){
				let clientProfileList = await pager.next()
				for(let clientProfile of clientProfileList){
					clientProfile.clientStage = clientProfile.clientStage.toLowerCase()
				}
				console.log("migrate: clientProfileList = ", clientProfileList)
				await dataManager.batchUpdateClientProfile(clientProfileList)
			}
		}

	}
}


export class Migration_14_15 extends Migration {


	constructor() {
		super(14, 15);

	}

	async migrate(dataManager, authManager) {
		console.log("migrating from 14 to 15")

		if(authManager.isCoach()){

			let dataFilter = new DataFilter()
			// .where("intakeFormId", "!=", null)//this requires extra index in firestore for team members. To keep it simple, we don't use this filter at all.

			let pager = await dataManager.getClientProfileListPager(dataFilter)

			while (pager.hasMore){
				let clientProfileList = await pager.next()
				for(let clientProfile of clientProfileList){
					clientProfile.clientStage = ClientStage.CLIENT
				}
				console.log("migrate: clientProfileList = ", clientProfileList)
				await dataManager.batchUpdateClientProfile(clientProfileList)
			}
		}
	}
}

export class Migration_13_14 extends Migration {


	constructor() {
		super(13, 14);
	}

	async migrate(dataManager, authManager) {
		console.log("migrating from 13 to 14")

		//use EventType.isIntakeFormEnabled instead of relying on FormItem.isEnabled

		if(authManager.isCoach()){

			let dataFilter = new DataFilter()
				// .where("intakeFormId", "!=", null)//this requires extra index in firestore for team members. To keep it simple, we don't use this filter at all.

			let eventTypePager = await dataManager.getEventTypeListPager(dataFilter)

			while (eventTypePager.hasMore){
				let eventTypeList = await eventTypePager.next()
				for(let eventType of eventTypeList){
					if(eventType.intakeFormId){
						let formItem = await dataManager.getFormItem(eventType.intakeFormId)
						if(formItem){
							eventType.isIntakeFormEnabled = formItem.isEnabled
						}
					}
				}
				console.log("migrate: eventTypeList = ", eventTypeList)
				await dataManager.batchUpdateEventType(eventTypeList)
			}
		}
	}
}

export class Migration_12_13 extends Migration {


	constructor() {
		super(12, 13);
	}

	async migrate(dataManager, authManager) {
		console.log("migrating from 12 to 13")
		//fixes client migration - for some reason migration of version 7 didn't apply
		if(authManager.isCoach()){
			let clientPager = await dataManager.getClientProfileListPager(new BaseDataFilter(100))
			console.log("migrate: clientPager = ", clientPager)
			while (clientPager.hasMore){
				let clientProfileList = await clientPager.next()
				for(let clientProfile of clientProfileList){
					clientProfile.userProfileUid = clientProfile.clientId

					if(clientProfile.clientJournalInviteStatus){
						clientProfile.portalInviteStatus = clientProfile.clientJournalInviteStatus
					}
					clientProfile.addSharedWithById(clientProfile.coachId)
				}
				console.log("migrate: clientProfileList = ", clientProfileList)
				await dataManager.batchUpdateClientProfile(clientProfileList)
			}
		}


	}
}
export class Migration_11_12 extends Migration {


	constructor() {
		super(11, 12);
	}

	async migrate(dataManager, authManager) {
		console.log("migrating from 11 to 12")

		// we are migrating PurchasedPackageItem to v5, ClientProfile to v7, JournalEntry to v2
		// this migration is performed on server as a one time global migration inside migrateTeamFeature

		// if(authManager.isOwner()){
		// 	//1. migrate PurchasedPackageItem to v5: participantIds -> sharedWithByIds
		// 	let dataFilter = new BaseDataFilter(100)
		// 		.where("schemaVersion", "<", 5)
		//
		// 	let purchasedPackagesPager = await dataManager.getPurchasedCoachingPackageListPager(dataFilter)
		// 	while(purchasedPackagesPager.hasMore){
		// 		let objectList = await purchasedPackagesPager.next()
		// 		//at this point migrateSchema is applied, so we just need to save it back to the database
		// 		await dataManager.batchUpdatePurchasedCoachingPackage(objectList)
		// 	}
		//
		// 	//2. migrate ClientProfile to v8: participantIds -> sharedWithByIds + filterable array=>object
		// 	//also renaming clientJournalInviteStatus to portalInviteStatus using clientProfile.migrateSchema mechanism
		//
		//
		// 	dataFilter = new BaseDataFilter(100)
		// 		.where("schemaVersion", "<", 8)
		// 	let clientPager = await dataManager.getClientProfileListPager(dataFilter)
		// 	while(clientPager.hasMore){
		// 		let clientList = await clientPager.next()
		// 		console.log("migrate: clientList = ", clientList)
		// 		//at this point ClientProfile.migrateSchema is applied, so we just need to save it back to the database
		//
		// 		//3. migrate journalEntries displayDestinationArray -> from array to object
		// 		for(let clientProfile of clientList){
		// 			let dataFilter = new BaseDataFilter(100)
		// 				.where("schemaVersion", "<", 2)
		// 				.where("clientId", "==", clientProfile.id)
		// 			let journalEntriesPager = await dataManager.getJournalEntryListPager(dataFilter)
		// 			while(journalEntriesPager.hasMore) {
		// 				let journalEntries = await journalEntriesPager.next()
		// 				//at this point JournalEntries.migrateSchema is applied, so we just need to save it back to the database
		// 				await dataManager.batchUpdateJournalEntry(journalEntries)
		// 			}
		// 		}
		// 		await dataManager.batchUpdateClientProfile(clientList)
		// 	}
		// }
	}
}


export class Migration_10_11 extends Migration {


	constructor() {
		super(10, 11);
	}

	async migrate(dataManager, authManager) {
		console.log("migrating from 10 to 11")

		//fix wheel of life responses to save emails

		if(authManager.isCoach()){
			let pager = await dataManager.getWheelOfLifeResponseListPager()
			while(pager.hasMore){
				let wheelResponses = await pager.next()
				for(let wheelResponse of wheelResponses){
					let clientProfile = await dataManager.getClientProfile(wheelResponse.clientId)
					if(clientProfile){
						wheelResponse.clientEmail = clientProfile.email
						if(wheelResponse.additionalInfo){
							wheelResponse.additionalInfo.email = clientProfile.email
						}
						//reset last exported to include in the "new responses" filter
						wheelResponse.isExported = false
						wheelResponse.lastExportedTs = 0
					}
				}
				await dataManager.batchSetWheelOfLifeResponse(wheelResponses)
			}
		}
	}
}

export class Migration_9_10 extends Migration {


	constructor() {
		super(9, 10);
	}

	async migrate(dataManager, authManager) {
		console.log("migrating from 9 to 10")

		//added isExported and lastExportedTs to WheelItem response for filtering

		if(authManager.isCoach()){
			let pager = await dataManager.getWheelOfLifeResponseListPager()
			while(pager.hasMore){
				let wheelResponses = await pager.next()
				await dataManager.batchSetWheelOfLifeResponse(wheelResponses)
			}
		}
	}
}

export class Migration_8_9 extends Migration {


	constructor() {
		super(8, 9);
	}

	async migrate(dataManager, authManager) {
		console.log("migrating from 8 to 9")
		if(authManager.isCoach()){
			let pager = await dataManager.getPurchasedCoachingPackageListPager()
			while(pager.hasMore){
				let purchasedPackagesList = await pager.next()

				for(let purchasedPackage of purchasedPackagesList){
					purchasedPackage.packageType = CoachingPackageType.ONE_ON_ONE
				}
				await dataManager.batchUpdatePurchasedCoachingPackage(purchasedPackagesList)
			}
		}
	}
}

export class Migration_7_8 extends Migration {


	constructor() {
		super(7, 8);
	}

	async migrate(dataManager, authManager) {
		console.log("migrating from 7 to 8")
		if(authManager.isCoach()){
			let clientMap = {}

			let baseDataFilter = new BaseDataFilter(100)
				.where("isCompleted", "==", false)

			let pager = await dataManager.getJournalEntryListPager(baseDataFilter)

			while(pager.hasMore){
				let journalEntriesArray = await pager.next()

				for(let journalEntry of journalEntriesArray){
					let clientId = journalEntry.clientId
					if(clientId && !clientMap[clientId]){
						clientMap[clientId] = await dataManager.getClientProfile(clientId)
					}
					let clientProfile = clientMap[clientId]
					let isEnabled = clientProfile ? clientProfile.isEnabled : true
					journalEntry.isClientEnabled = isEnabled
				}
				await dataManager.batchUpdateJournalEntry(journalEntriesArray)
			}
			console.log("clientMap = ", clientMap)
		}
	}
}
export class Migration_6_7 extends Migration {


	constructor() {
		super(6, 7);
	}

	async migrate(dataManager, authManager) {
		console.log("migrating from 6 to 7")
		if(authManager.isCoach()){
			await dataManager.apiPostCalendarEnableWatchers()
		}
	}
}


export class Migration_5_6 extends Migration {


	constructor() {
		super(5, 6);
	}

	async migrate(dataManager, authManager) {
		console.log("migrating from 5 to 6")
		if(authManager.isCoach()){
			let wheelResponseArray = await dataManager.getWheelOfLifeResponseList()
			let companyIndex = await dataManager.getCompanyIndex() || new CompanyIndexItem()
			let clientsCache = {}
			for(let wheelResponse of wheelResponseArray){
				let clientId = wheelResponse.clientId
				let clientProfile = clientsCache[clientId]
				console.log("migrating from 5 to 6: clientId = ", clientId, ", wheelResponse = ", wheelResponse)
				if(!clientProfile){
					clientProfile = await dataManager.getClientProfile(clientId)
					clientsCache[clientId] = clientProfile
				}

				if(!clientProfile){
					continue
				}

				wheelResponse.company = clientProfile.company
				wheelResponse.company_casefolded = clientProfile.company
				companyIndex.addCompany(clientProfile.company)
				if(wheelResponse.autoTagsData.isEnabled){
					wheelResponse.tag = wheelResponse.autoTagsData.tags
				}
			}

			await dataManager.batchSetWheelOfLifeResponse(wheelResponseArray)
			await dataManager.setCompanyIndex(companyIndex)

			await this.fixClientProfileIndex(dataManager)
		}
	}

	async fixClientProfileIndex(dataManager){
		// there was regression on around 30.01.2023 where search keywords index wasn't building for clientProfile
		// so we are rebuilding it here
		let res = await dataManager.getClientProfileListRef()
			.where('createdAt', '>', 1674220350000) //20.01.2023 - grab all clients from that date and fix them
			.get()
		let clientProfileArray = dataManager.resArrayToDataArray(res, ClientProfile)

		console.log("fixClientProfileIndex: clientProfileArray = ", clientProfileArray)

		//just save to the database to trigger beforeSaveToDB() which will rebuild search index
		await dataManager.batchUpdateClientProfile(clientProfileArray)
	}
}

export class Migration_4_5 extends Migration {


	constructor() {
		super(4, 5);
	}

	async migrate(dataManager, authManager) {
		console.log("migrating from 4 to 5")
		if(authManager.isCoach()){
			let calendarIntegrations = await dataManager.apiGetCalendarIntegrations()
			let userProfile = authManager.getUserProfile()
			if(calendarIntegrations.gCalendarItemsArray.length > 0 ){
				userProfile.integrationsList.gmailIntegration = IntegrationStatus.INTEGRATED
			}
			if(calendarIntegrations.outlook365ItemsArray.length > 0 ){
				userProfile.integrationsList.outlookIntegration = IntegrationStatus.INTEGRATED
			}
			await authManager.updateUserProfile(userProfile)
		}
	}
}

export class Migration_3_4 extends Migration {


	constructor() {
		super(3, 4);
	}

	async migrate(dataManager, authManager) {
		console.log("migrating from 3 to 4")
		if(authManager.isCoach()){
			let clientPager = await dataManager.getClientProfileListPager(new BaseDataFilter(500))
			console.log("migrate: clientPager = ", clientPager)
			while (clientPager.hasMore){
				let clientProfileList = await clientPager.next()
				console.log("migrate: clientProfileList = ", clientProfileList)
				await dataManager.batchUpdateClientProfile(clientProfileList)
			}
		}
	}
}

export class Migration_2_3 extends Migration {


	constructor() {
		super(2, 3);
	}

	async migrate(dataManager, authManager) {
		console.log("migrating from 2 to 3")
		if(authManager.isCoach()){
			let sessionTypes = await dataManager.listSessionTypes()
			for(let eventType of sessionTypes){
				await dataManager.setEventType(eventType)
			}
		}
	}
}
export class Migration_1_2 extends Migration {


	constructor() {
		super(1, 2);
	}

	async migrate(dataManager, authManager) {
		console.log("migrating from 1 to 2")
		if(authManager.isCoach()){
			await dataManager.apiPostRecalculateClientProfilesCount()
		}
	}
}

export class Migration_0_1 extends Migration {


	constructor() {
		super(0, 1);
	}

	async migrate(dataManager) {
		console.log("migrating from 0 to 1")
		// set funnel id to a specific value
		// change funnel items from clientProfile to funnelEntry
		// move all funnelEntries to a separate collection


		let funnel = await dataManager.getFunnelItem()
		if(!funnel){
			return
		}
		funnel.id = "--mainFunnel--"

		let entries = []
		let maxIndex = new Date().getTime() * 10000
		for(let stage of funnel.stages){
			if(isIterable(stage.clientProfiles)){
				let index = 0
				let len = stage.clientProfiles.length
				for(let clientProfile of stage.clientProfiles){
					let funnelEntry = stage.createFunnelEntry()
					funnelEntry.index = Math.round(index/len * maxIndex)
					funnelEntry.clientProfile = plainToClass(ClientProfile, clientProfile)
					entries.push(funnelEntry)
					index++
				}
			}
			delete stage.clientProfiles
		}
		await dataManager.setFunnel(funnel)
		await dataManager.batchSetFunnelEntry(funnel.id, entries)
	}
}
